// File: src/intake/app/coachTyping.js
// Purposes: Provide reusable “human typing” utilities for coach-style UI text (delay logic + typing + in-view trigger)
// Details:
// - coachHumanDelay(char, opts):
//   - Returns a human-like delay in ms for each typed character
//   - Supports base speed ranges, punctuation pauses, uppercase emphasis, newline pauses, and random thinking pauses
// - coachTypeInto(el, fullText, opts):
//   - Types a string into a DOM element character by character (one-time per element)
//   - Adds/removes CSS classes and data attributes to indicate typing/typed state
//   - Supports paragraph-aware pauses when detecting "\n\n"
// - coachObserveAndType(elements, getText, opts):
//   - Uses IntersectionObserver to start typing only when elements come into view
//   - Ensures typing runs only once per element
//   - Adds optional aria-live attributes for accessibility announcements
// - Safe behavior: only modifies the provided DOM element (classes + dataset + aria attrs)
// Flow:
// - Coach-style UI components decide whether typing should occur immediately or only when visible
// - When typing is needed:
//   - coachTypeInto(el, text) is used to progressively write text into the element
//   - coachHumanDelay() is called per character to determine pacing
// - For “type on scroll into view” scenarios:
//   - a component collects target elements and calls coachObserveAndType(elements, getText)
//   - IntersectionObserver detects when an element enters viewport
//   - coachObserveAndType() triggers coachTypeInto() once for that element
// - After typing completes:
//   - element gets data-coachTyped="1" and "digivibe-coach-typed" class
//   - the optional onDone callback can run for follow-up behavior

/**
 * Create a human-like delay in milliseconds for a given character.
 *
 * Big idea:
 * - Real typing is never perfectly constant.
 * - Some characters naturally cause a pause:
 *   - punctuation
 *   - question/exclamation marks
 *   - uppercase letters
 *   - line breaks
 * - We also inject rare random “thinking pauses” to avoid a robotic rhythm.
 *
 * Important:
 * - This function only knows about the CURRENT character.
 * - It does NOT inspect the whole string or detect paragraph breaks by itself.
 * - Double-newline paragraph pauses are handled in coachTypeInto().
 *
 * @param {string} char
 * @param {Object} opts
 * @returns {number}
 */
export function coachHumanDelay(char, opts = {}) {
  const {
    // BASE SPEED (applies to every character)
    // min/max define the normal per-character typing delay range in milliseconds.
    min = 24,
    max = 41,

    // UPPERCASE EMPHASIS
    // Uppercase letters often feel a bit more deliberate when "typed".
    uppercaseExtraMin = 12,
    uppercaseExtraMax = 30,

    // PUNCTUATION PAUSE (.,;:)
    // Small extra pause after punctuation to create sentence rhythm.
    punctuationExtraMin = 140,
    punctuationExtraMax = 360,

    // BANG / QUESTION PAUSE (!?)
    // Stronger punctuation usually feels like a larger expressive pause.
    bangExtraMin = 220,
    bangExtraMax = 540,

    // NEWLINE PAUSE (\n)
    // Adds a short pause when a line break is typed.
    newlineExtraMin = 220,
    newlineExtraMax = 480,

    // DOUBLE NEWLINE PAUSE (\n\n) — paragraphs
    // This is declared here as a shared option shape,
    // but the actual paragraph-break detection happens in coachTypeInto().
    doubleNewlineExtraMin = 240,
    doubleNewlineExtraMax = 560,

    // RANDOM “THINKING” PAUSE
    // Chance to add a random pause on any given character.
    // 0.02 means roughly ~2% chance per typed character.
    thinkChance = 0.02,

    // THINK PAUSE RANGE
    // Delay added when thinkChance triggers.
    thinkExtraMin = 300,
    thinkExtraMax = 950,
  } = opts;

  // Start with a base random delay inside the normal typing range.
  let d = min + Math.random() * (max - min);

  // Uppercase emphasis:
  // Only apply if the current character is a real A-Z uppercase letter.
  if (char && char.toUpperCase() === char && /[A-Z]/.test(char)) {
    d += uppercaseExtraMin + Math.random() * (uppercaseExtraMax - uppercaseExtraMin);
  }

  // Extra pause after regular punctuation.
  if (/[.,;:]/.test(char)) {
    d += punctuationExtraMin + Math.random() * (punctuationExtraMax - punctuationExtraMin);
  }

  // Even longer pause after ! or ? for stronger expression.
  if (/[!?]/.test(char)) {
    d += bangExtraMin + Math.random() * (bangExtraMax - bangExtraMin);
  }

  // Add a pause when typing a newline.
  if (char === '\n') {
    d += newlineExtraMin + Math.random() * (newlineExtraMax - newlineExtraMin);
  }

  // Sometimes inject a random thinking pause, regardless of character type.
  if (Math.random() < thinkChance) {
    d += thinkExtraMin + Math.random() * (thinkExtraMax - thinkExtraMin);
  }

  // NOTE:
  // Paragraph-aware extra delay for "\n\n" is not handled here,
  // because this function only receives one character at a time.
  return Math.round(d);
}

/**
 * Type text into a DOM element once.
 *
 * One-time safety is enforced through dataset flags on the element itself:
 * - el.dataset.coachTyped === '1'  => typing already finished once
 * - el.dataset.coachTyping === '1' => typing is currently in progress
 *
 * Why this is useful:
 * - Prevents accidental double-starts when multiple callers or observers
 *   try to trigger typing for the same element.
 *
 * Important limitations:
 * - This is a simple DOM typing helper.
 * - It types plain text via textContent, not HTML.
 * - So it does NOT render inline bold/markup like the custom Coach.jsx component does.
 *
 * @param {HTMLElement} el
 * @param {string} fullText
 * @param {Object} opts
 * @returns {void}
 */
export function coachTypeInto(el, fullText, opts = {}) {
  if (!el) return;

  // Prevent starting twice on the same element.
  if (el.dataset.coachTyped === '1' || el.dataset.coachTyping === '1') return;

  const { delayOptions = {}, onDone = null, cursorClass = 'digivibe-coach-typing' } = opts;

  // Mark the element as actively typing and add the typing/cursor class.
  el.dataset.coachTyping = '1';
  el.classList.add(cursorClass);

  // Clear the element first so the content visibly "appears" as typed.
  el.textContent = '';

  // Current character index.
  let i = 0;

  /**
   * One typing step.
   *
   * Responsibilities:
   * - write the next slice of the text
   * - compute the delay for the current character
   * - detect paragraph breaks (\n\n) for an extra pause
   * - schedule the next step
   * - finalize state when complete
   */
  const tick = () => {
    // If the element has been removed from the DOM, stop safely.
    if (!el.isConnected) return;

    // While we still have content to type, keep writing incrementally.
    if (i <= fullText.length) {
      el.textContent = fullText.slice(0, i);

      // Current and previous characters are used to detect paragraph breaks.
      const char = fullText.charAt(i - 1) || '';
      const prevChar = fullText.charAt(i - 2) || '';
      i++;

      // Extra pause when we detect a paragraph break (\n\n).
      // This makes a blank-line break feel more intentional and readable.
      let extra = 0;
      if (char === '\n' && prevChar === '\n') {
        const { doubleNewlineExtraMin = 240, doubleNewlineExtraMax = 560 } = delayOptions || {};

        extra =
          doubleNewlineExtraMin + Math.random() * (doubleNewlineExtraMax - doubleNewlineExtraMin);
      }

      // Schedule the next typing step based on:
      // - the current character delay
      // - plus optional paragraph-break extra delay
      window.setTimeout(tick, coachHumanDelay(char, delayOptions) + Math.round(extra));
      return;
    }

    // Finished typing:
    // - mark the element as typed
    // - clear the typing flag
    // - swap CSS classes
    el.dataset.coachTyped = '1';
    el.dataset.coachTyping = '';
    el.classList.remove(cursorClass);
    el.classList.add('digivibe-coach-typed');

    // Optional callback so callers can chain follow-up behavior.
    if (typeof onDone === 'function') onDone(el);
  };

  // Start immediately.
  tick();
}

/**
 * Observe a list of elements and start typing when each element enters the viewport.
 *
 * Why this exists:
 * - Some coach text should not start typing immediately on page load.
 * - Instead, it should wait until the user scrolls it into view.
 *
 * Safety behavior:
 * - Each element is typed only once.
 * - Once an element is triggered, it is unobserved.
 *
 * Accessibility:
 * - Optionally adds aria-live / aria-atomic so screen readers can announce updates.
 *
 * @param {HTMLElement[]} elements
 * @param {(el: HTMLElement) => string} getText
 * @param {Object} opts
 * @returns {() => void} cleanup function
 */
export function coachObserveAndType(elements, getText, opts = {}) {
  const {
    // Threshold controls how much of the element should be visible before it counts as "in view".
    threshold = 0.2,

    // rootMargin slightly adjusts the effective viewport bounds.
    // Negative bottom margin makes the trigger feel a bit earlier/more intentional.
    rootMargin = '0px 0px -10% 0px',

    delayOptions = {},
    cursorClass = 'digivibe-coach-typing',

    // Accessibility options:
    // aria-live controls how assistive tech announces dynamic updates.
    ariaLive = 'polite', // 'off' | 'polite' | 'assertive'

    // Kept as a string intentionally to avoid React boolean serialization inconsistencies
    // in environments where these attributes are mirrored later.
    ariaAtomic = 'false',
  } = opts;

  // If there is nothing usable to observe, return a no-op cleanup.
  if (!Array.isArray(elements) || elements.length === 0) return () => {};

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        // Ignore entries that are not currently intersecting the viewport.
        if (!entry.isIntersecting) continue;

        const el = entry.target;

        // If already typed earlier, stop observing this element and move on.
        if (el.dataset.coachTyped === '1') {
          observer.unobserve(el);
          continue;
        }

        // Accessibility:
        // If enabled, announce live text updates in a screen-reader-friendly way.
        if (ariaLive && ariaLive !== 'off') {
          el.setAttribute('aria-live', ariaLive);
          el.setAttribute('aria-atomic', ariaAtomic);
        }

        // Ask the caller what text belongs to this element.
        const fullText = typeof getText === 'function' ? getText(el) || '' : '';

        // Start typing the element content.
        coachTypeInto(el, fullText, { delayOptions, cursorClass });

        // This element only needs to trigger once.
        observer.unobserve(el);
      }
    },
    { threshold, rootMargin }
  );

  // Register every provided element with the observer.
  for (const el of elements) {
    if (el) observer.observe(el);
  }

  // Return cleanup function so callers can disconnect the observer on unmount.
  return () => observer.disconnect();
}
